import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "../../../firebase/config";

import PrimaryButton from "../../../components/Buttons/PrimaryButton";

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [verifying, setVerifying] = useState<boolean>(true);
  const [email, setEmail] = useState<string>("");

  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setError("Invalid or missing reset code.");
        setVerifying(false);
        return;
      }

      try {
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        setVerifying(false);
      } catch (error: any) {
        console.error("Code verification error:", error);

        if (error.code === "auth/invalid-action-code") {
          setError(
            "This password reset link is invalid or has already been used.",
          );
        } else if (error.code === "auth/expired-action-code") {
          setError(
            "This password reset link has expired. Please request a new one.",
          );
        } else {
          setError("Failed to verify reset link. Please try again.");
        }
        setVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!oobCode) {
      setError("Invalid reset code.");
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordReset(auth, oobCode, password);

      navigate("/login", {
        state: {
          message:
            "Password reset successful! Please log in with your new password.",
        },
      });
    } catch (error: any) {
      console.error("Password reset error:", error);

      if (error.code === "auth/invalid-action-code") {
        setError(
          "This password reset link is invalid or has already been used.",
        );
      } else if (error.code === "auth/expired-action-code") {
        setError(
          "This password reset link has expired. Please request a new one.",
        );
      } else if (error.code === "auth/weak-password") {
        setError("Password is too weak. Please choose a stronger password.");
      } else {
        setError("Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-white">
        <div className="w-full max-w-sm px-4">
          <h2 className="text-xl font-semibold tracking-tight text-gray-900 mb-1">
            Verifying...
          </h2>
          <p className="text-sm text-gray-500">
            Please wait while we verify your reset link.
          </p>
        </div>
      </div>
    );
  }

  if (error && !email) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-white">
        <div className="w-full max-w-sm px-4">
          <h2 className="text-xl font-semibold tracking-tight text-gray-900 mb-1">
            Invalid Reset Link
          </h2>
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {error}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Please request a new password reset link from the login page.
          </p>
          <PrimaryButton
            className="w-full"
            onClick={() => navigate("/forgotPassword")}
          >
            Request New Reset Link
          </PrimaryButton>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex justify-center items-center bg-white">
      <div className="w-full max-w-sm px-4">
        <h2 className="text-xl font-semibold tracking-tight text-gray-900 mb-1">
          Create new password
        </h2>
        <p className="text-sm text-gray-500 mb-1">
          Enter a new password for your account.
        </p>
        {email && (
          <p className="text-sm text-gray-500 mb-6">Account: {email}</p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              New Password
            </label>
            <input
              type="password"
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
              minLength={6}
              placeholder="Enter new password"
            />
            <p className="text-xs text-gray-400 mt-1">
              Must be at least 6 characters long
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Confirm Password
            </label>
            <input
              type="password"
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              required
              placeholder="Confirm new password"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <PrimaryButton className="w-full" type="submit" disabled={loading}>
            {loading ? "Resetting..." : "Reset Password"}
          </PrimaryButton>
        </form>

        <div className="border-t border-gray-100 mt-6 pt-6 text-center">
          <p className="text-sm text-gray-500">
            Remember your password?{" "}
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-[#1d365d] font-medium hover:text-[#2d4a75] transition-colors"
              disabled={loading}
            >
              Back to login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
